import React from "react";
import { Label, InputText, Dropdown } from "../../../../components/Inputs";
import AddressForm from "./AddressForm";
import { SecondaryButton } from "../../../../components/Buttons";

const RegionForm = React.forwardRef(function RegionForm(
  {
    className = "",
    model,
    regions = ["1", "2", "3", "4", "7", "9"],
    isCompanySpecialKDL,
    disabled,
    getAddress,
    minNrDebitors = 0,
  },
  ref,
) {
  const addressFormRef = React.useRef(null);

  const [state, setState] = React.useState(() => {
    const regionModel = model || {};
    const companyName = regionModel.companyName || "";
    const address = {
      name: companyName,
      address: regionModel.address || {},
    };
    return {
      name: regionModel.name || "",
      storageLocation: regionModel.storageLocation || "",
      debitorKey: regionModel.debitorKey || "",
      debitorMax: isCompanySpecialKDL ? 1 : regionModel.debitorMax || 10,
      address,
      error: {
        debitorMax: null,
      },
    };
  });

  const validate = (noValidate) => {
    const { error, address: stateAddress, ...modelData } = state;
    const newError = { ...error };
    let isValid = true;
    // name
    if (modelData.name === "") {
      newError.name = true;
      isValid = false;
    }
    // debitorKey
    if (!isCompanySpecialKDL && modelData.debitorKey === "") {
      newError.debitorKey = true;
      isValid = false;
    }
    // debitorMax
    modelData.debitorMax = parseInt(modelData.debitorMax);
    if (isNaN(modelData.debitorMax)) {
      newError.debitorMax = true;
      isValid = false;
    } else if (modelData.debitorMax < minNrDebitors) {
      const isPlural = minNrDebitors > 1;
      newError.debitorMax = `Es gibt schon ${minNrDebitors} Debitor${
        isPlural ? "en" : ""
      } für diese Region`;
      isValid = false;
    }
    // address
    const addressModel = addressFormRef.current.getModel();
    if (addressModel) {
      const oldAddress = (model || {}).address || {};
      const { name, address } = addressModel;
      modelData.companyName = name;
      modelData.address = { ...oldAddress.address, ...address };
    }
    if (!noValidate && !isValid) {
      setState((prev) => ({ ...prev, error: newError }));
      return null;
    }
    const { id, index } = model;
    return { id, index, ...modelData };
  };

  React.useImperativeHandle(ref, () => ({
    getModel: (noValidate) => validate(noValidate),
    getData: () => validate(true),
  }));

  const copyCompanyAddress = () => {
    const address = getAddress ? getAddress() : null;
    address && setState((prev) => ({ ...prev, address }));
  };

  const { name, storageLocation, debitorKey, debitorMax, address, error } =
    state;
  let RegionOptions = [];
  if (name !== "" && !regions.find((item) => item === name)) {
    let newRegions = [...regions];
    newRegions.push(name);
    newRegions.sort();
    RegionOptions = newRegions.map((item) => ({ label: item, value: item }));
  } else {
    RegionOptions = [{ label: "Bitte wählen", value: "" }].concat(
      regions.map((item) => ({ label: item, value: item })),
    );
  }
  const debitorMaxOptions = [
    { label: "0", value: 0 },
    { label: "1", value: 1 },
  ];
  return (
    <div className={`${className}`}>
      <div className="w3-row neg-margin-lr">
        <div className={`w3-col s6 pad-lr`}>
          <h4 className="no-margin">{`Grunddaten`}</h4>
          <div
            className={`form-group-item pad-top${
              error.name ? " error-group" : ""
            }`}
          >
            <Label htmlFor="name">{`Name*`}</Label>
            {disabled ? (
              <InputText
                id="nameId"
                data-test-id="nameId"
                className="w3-block w3-border"
                value={name}
                disabled
              />
            ) : (
              <Dropdown
                id="nameId"
                data-test-id="nameId"
                className="w3-block w3-border"
                value={name}
                options={RegionOptions}
                onChange={(event) => {
                  const value = event.value;
                  setState((prev) => ({ ...prev, name: value }));
                }}
                showClear
                style={{ height: "40px" }}
              />
            )}
          </div>
          {isCompanySpecialKDL && (
            <div
              className={`form-group-item pad-top${
                error.storageLocation ? " error-group" : ""
              }`}
            >
              <Label htmlFor="storageLocation">{`Storage Location`}</Label>
              <InputText
                id="storageLocation"
                data-test-id="storageLocation"
                className="w3-block w3-border"
                value={storageLocation}
                disabled={disabled}
                onChange={(event) => {
                  const value = event.target.value;
                  setState((prev) => ({ ...prev, storageLocation: value }));
                }}
              />
            </div>
          )}
          {!isCompanySpecialKDL && (
            <div className="w3-row neg-margin-lr">
              <div className="w3-col s6 pad-lr">
                <div
                  className={`form-group-item pad-top${
                    error.storageLocation ? " error-group" : ""
                  }`}
                >
                  <Label htmlFor="storageLocation">{`Storage Location`}</Label>
                  <InputText
                    id="storageLocation"
                    data-test-id="storageLocation"
                    className="w3-block w3-border"
                    value={storageLocation}
                    disabled={disabled}
                    onChange={(event) => {
                      const value = event.target.value;
                      setState((prev) => ({ ...prev, storageLocation: value }));
                    }}
                  />
                </div>
              </div>
              <div className={`w3-rest pad-lr`}>
                <div
                  className={`form-group-item pad-top${
                    error.debitorKey ? " error-group" : ""
                  }`}
                >
                  <Label htmlFor="debitorKey">{`Debitor-Key*`}</Label>
                  <InputText
                    id="debitorKey"
                    data-test-id="debitorKey"
                    className="w3-block w3-border"
                    value={debitorKey}
                    disabled={disabled}
                    onChange={(event) => {
                      const value = event.target.value;
                      setState((prev) => ({ ...prev, debitorKey: value }));
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div
            className={`form-group-item pad-top${
              error.debitorMax ? " error-group" : ""
            }`}
          >
            <Label htmlFor="debitorMax">{`Anzahl an erlaubten Debitoren*`}</Label>
            {isCompanySpecialKDL ? (
              <Dropdown
                id="debitorMax"
                data-test-id="debitorMax"
                className="w3-block w3-border"
                value={debitorMax}
                options={debitorMaxOptions}
                onChange={(event) => {
                  const value = event.value;
                  setState((prev) => ({ ...prev, debitorMax: value }));
                }}
                showClear
              />
            ) : (
              <InputText
                id="debitorMax"
                data-test-id="debitorMax"
                className="w3-block w3-border"
                value={debitorMax}
                type="number"
                disabled={disabled}
                onChange={(event) => {
                  const value = event.target.value;
                  setState((prev) => ({ ...prev, debitorMax: value }));
                }}
              />
            )}
            {error.debitorMax && error.debitorMax !== true && (
              <span className="error-text">{error.debitorMax}</span>
            )}
          </div>
        </div>
        <div className={`w3-col s6 pad-lr`}>
          <h4 className="no-margin">{`Lieferadresse`}</h4>
          <AddressForm
            ref={addressFormRef}
            model={address}
            isVertical
            resetState
            disabled={disabled}
            namePrefix="region_"
            hideName
          />
          {!disabled && (
            <div className="pos-rel pad-top form-group-item">
              <SecondaryButton
                style={{ position: "absolute", bottom: 0, left: 0 }}
                onClick={copyCompanyAddress}
              >
                {"Firmenadresse verwenden"}
              </SecondaryButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default RegionForm;
